// Mock data for demo API calls - only active in demo mode
import { mockCuratedReports, mockCuratedReportsDataSource } from '@Shared/testUtils';

import { CrMetricDriverPriorityQuadrantChartData } from '@Shared/models/CuratedReports/CrMetricDriverPriorityQuadrantChartData';
import { CrChartData } from '@Shared/models/CuratedReports/CrChartData';
import { CrClusteredColumnChartData } from '@Shared/models/CuratedReports/CrClusteredColumnChartData';
import { CrDeltaBarChartData } from '@Shared/models/CuratedReports/CrDeltaBarChartData';
import { CrMetricDeltaQuadrantChartData } from '@Shared/models/CuratedReports/CrMetricDeltaQuadrantChartData';
import { CrMetricMovementTableData } from '@Shared/models/CuratedReports/CrMetricMovementTableData';
import { CrMetricTileTableData } from '@Shared/models/CuratedReports/CrMetricTileTableData';
import { CrNpsGaugeChartData } from '@Shared/models/CuratedReports/CrNpsGaugeChartData';
import { CrNpsStackedBarWithLineChartData } from '@Shared/models/CuratedReports/CrNpsStackedBarWithLineChartData';
import { CrSmoothedLineChartData } from '@Shared/models/CuratedReports/CrSmoothedLineChartData';

// Global flag to track demo mode state - set by router guards
let isDemoModeActive = false;

/**
 * Set the global demo mode state
 */
export function setDemoMode(active: boolean): void {
  isDemoModeActive = active;
}

/**
 * Check if we're in demo mode
 */
export function isInDemoMode(): boolean {
  return isDemoModeActive;
}

/**
 * Initialize demo-specific mocking
 * In demo mode, we override the API functions to return mock data
 */
export function initializeDemoMocking() {
  if (isInDemoMode()) {
    // Demo mode active — API calls will return mock data
  }
}

/**
 * Get mock data for reports
 */
export function getMockReports() {
  return isInDemoMode() ? mockCuratedReports : [];
}

/**
 * Get mock data for report config
 */
export async function getMockReportConfig() {
  return isInDemoMode() ? mockCuratedReportsDataSource : null;
}

/**
 * Get mock data for slide data
 */
export async function getMockSlideData(
  visualType?: string,
  visualKey?: string
): Promise<
  | CrMetricDriverPriorityQuadrantChartData
  | CrClusteredColumnChartData
  | CrChartData
  | CrDeltaBarChartData
  | CrMetricDeltaQuadrantChartData
  | CrMetricMovementTableData
  | CrMetricTileTableData
  | CrNpsGaugeChartData
  | CrNpsStackedBarWithLineChartData
  | CrSmoothedLineChartData
  | { renderedText: string }
  | null
> {
  if (!isInDemoMode()) {
    return null;
  }

  // Return appropriate mock data based on visual type
  if (visualType === 'CrBottomUnitsChart') {
    return getMockBottomUnitsData();
  }

  if (visualType === 'CrClusteredColumnChart') {
    return getMockClusteredColumnChartData();
  }

  if (visualType === 'CrDeltaBarChart') {
    return getMockDeltaBarChartData();
  }

  if (visualType === 'CrMetricDeltaQuadrantChart') {
    return getMockMetricDeltaQuadrantChartData();
  }

  if (visualType === 'CrNpsGaugeChart') {
    return getMockNpsGaugeChartData();
  }

  if (visualType === 'CrTextTemplate') {
    return {
      renderedText: getMockTextForVisualKey(visualKey),
    };
  }

  if (visualType === 'CrTopUnitsChart') {
    return getMockTopUnitsData();
  }

  if (visualType === 'CrMetricMovementTable') {
    return getMockMetricMovementTableData();
  }

  if (visualType === 'CrMetricTileTable') {
    return getMockMetricTileTableData();
  }

  if (visualType === 'CrMetricDriverPriorityQuadrantChart') {
    return getMockCrMetricDriverPriorityQuadrantChartData();
  }

  if (visualType === 'CrSmoothedLineChart') {
    return getMockSmoothedLineChartData();
  }

  // Default to NPS data for backward compatibility
  return getMockNpsStackedBarWithLineChartData();
}

/**
 * Get mock data for ClusteredColumnChart slide data
 */
export async function getMockClusteredColumnChartData(): Promise<CrClusteredColumnChartData> {
  // Return mock sales performance data in the new format with rowDimension
  return {
    visualType: 'CrClusteredColumnChart',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          Msr_Q1_Sales: { CurrentValue: 428.0 },
          Msr_Q2_Sales: { CurrentValue: 535.0 },
          Msr_Q3_Sales: { CurrentValue: 595.0 },
          Msr_Q4_Sales: { CurrentValue: 655.0 },
          RecordCount: { CurrentValue: 16.0 },
        },
        rowDimension: {
          key: 'Region',
          values: [
            {
              value: 'North',
              totalMeasures: {
                Msr_Q1_Sales: { CurrentValue: 120 },
                Msr_Q2_Sales: { CurrentValue: 135 },
                Msr_Q3_Sales: { CurrentValue: 150 },
                Msr_Q4_Sales: { CurrentValue: 165 },
                RecordCount: { CurrentValue: 4 },
              },
            },
            {
              value: 'South',
              totalMeasures: {
                Msr_Q1_Sales: { CurrentValue: 98 },
                Msr_Q2_Sales: { CurrentValue: 115 },
                Msr_Q3_Sales: { CurrentValue: 130 },
                Msr_Q4_Sales: { CurrentValue: 145 },
                RecordCount: { CurrentValue: 4 },
              },
            },
            {
              value: 'East',
              totalMeasures: {
                Msr_Q1_Sales: { CurrentValue: 145 },
                Msr_Q2_Sales: { CurrentValue: 160 },
                Msr_Q3_Sales: { CurrentValue: 175 },
                Msr_Q4_Sales: { CurrentValue: 190 },
                RecordCount: { CurrentValue: 4 },
              },
            },
            {
              value: 'West',
              totalMeasures: {
                Msr_Q1_Sales: { CurrentValue: 110 },
                Msr_Q2_Sales: { CurrentValue: 125 },
                Msr_Q3_Sales: { CurrentValue: 140 },
                Msr_Q4_Sales: { CurrentValue: 155 },
                RecordCount: { CurrentValue: 4 },
              },
            },
          ],
        },
      },
      dimensionMetadata: {
        Region: { friendlyName: 'Region' },
      },
      measureMetadata: {
        Msr_Q1_Sales: { friendlyName: 'Q1 2024', format: '0,0' },
        Msr_Q2_Sales: { friendlyName: 'Q2 2024', format: '0,0' },
        Msr_Q3_Sales: { friendlyName: 'Q3 2024', format: '0,0' },
        Msr_Q4_Sales: { friendlyName: 'Q4 2024', format: '0,0' },
        RecordCount: { friendlyName: 'Responses', format: '0' },
      },
    },
    count: 4,
  };
}

/**
 * Get mock data for TopUnits visual
 */
export async function getMockTopUnitsData(): Promise<CrChartData> {
  return {
    visualType: 'CrTopUnitsChart',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          Msr_Sales_Performance: { CurrentValue: 1045.0 },
          RecordCount: { CurrentValue: 1209.0 },
        },
        columnDimension: {
          key: 'StoreName',
          values: [
            {
              value: 'Store A',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 95 }, RecordCount: { CurrentValue: 120 } },
            },
            {
              value: 'Store B',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 88 }, RecordCount: { CurrentValue: 89 } },
            },
            {
              value: 'Store C',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 92 }, RecordCount: { CurrentValue: 95 } },
            },
            {
              value: 'Store D',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 87 }, RecordCount: { CurrentValue: 67 } },
            },
            {
              value: 'Store E',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 93 }, RecordCount: { CurrentValue: 110 } },
            },
            {
              value: 'Store F',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 89 }, RecordCount: { CurrentValue: 78 } },
            },
            {
              value: 'Store G',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 91 }, RecordCount: { CurrentValue: 92 } },
            },
            {
              value: 'Store H',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 96 }, RecordCount: { CurrentValue: 134 } },
            },
            {
              value: 'Store I',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 85 }, RecordCount: { CurrentValue: 45 } },
            },
            {
              value: 'Store J',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 94 }, RecordCount: { CurrentValue: 98 } },
            },
            {
              value: 'Store K',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 86 }, RecordCount: { CurrentValue: 56 } },
            },
            {
              value: 'Store L',
              totalMeasures: { Msr_Sales_Performance: { CurrentValue: 90 }, RecordCount: { CurrentValue: 75 } },
            },
          ],
        },
      },
      dimensionMetadata: {
        StoreName: { friendlyName: 'Store Names' },
      },
      measureMetadata: {
        Msr_Sales_Performance: { friendlyName: 'Sales Performance', format: '0,0' },
        RecordCount: { friendlyName: 'Responses', format: '0' },
      },
    },
    count: 12,
  };
}

/**
 * Get mock data for BottomUnits visual
 */
export async function getMockBottomUnitsData(): Promise<CrChartData> {
  return {
    visualType: 'CrBottomUnitsChart',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          Performance_Index: { CurrentValue: 99.0 },
          RecordCount: { CurrentValue: 1209.0 },
        },
        columnDimension: {
          key: 'StoreName',
          values: [
            {
              value: 'Store A',
              totalMeasures: { Performance_Index: { CurrentValue: -15 }, RecordCount: { CurrentValue: 120 } },
            },
            {
              value: 'Store B',
              totalMeasures: { Performance_Index: { CurrentValue: -8 }, RecordCount: { CurrentValue: 89 } },
            },
            {
              value: 'Store C',
              totalMeasures: { Performance_Index: { CurrentValue: -3 }, RecordCount: { CurrentValue: 95 } },
            },
            {
              value: 'Store D',
              totalMeasures: { Performance_Index: { CurrentValue: 2 }, RecordCount: { CurrentValue: 67 } },
            },
            {
              value: 'Store E',
              totalMeasures: { Performance_Index: { CurrentValue: 5 }, RecordCount: { CurrentValue: 110 } },
            },
            {
              value: 'Store F',
              totalMeasures: { Performance_Index: { CurrentValue: 8 }, RecordCount: { CurrentValue: 78 } },
            },
            {
              value: 'Store G',
              totalMeasures: { Performance_Index: { CurrentValue: 12 }, RecordCount: { CurrentValue: 92 } },
            },
            {
              value: 'Store H',
              totalMeasures: { Performance_Index: { CurrentValue: 15 }, RecordCount: { CurrentValue: 134 } },
            },
            {
              value: 'Store I',
              totalMeasures: { Performance_Index: { CurrentValue: 18 }, RecordCount: { CurrentValue: 45 } },
            },
            {
              value: 'Store J',
              totalMeasures: { Performance_Index: { CurrentValue: 22 }, RecordCount: { CurrentValue: 98 } },
            },
            {
              value: 'Store K',
              totalMeasures: { Performance_Index: { CurrentValue: 25 }, RecordCount: { CurrentValue: 56 } },
            },
            {
              value: 'Store L',
              totalMeasures: { Performance_Index: { CurrentValue: 28 }, RecordCount: { CurrentValue: 75 } },
            },
          ],
        },
      },
      dimensionMetadata: {
        StoreName: { friendlyName: 'Store Names' },
      },
      measureMetadata: {
        Performance_Index: { friendlyName: 'Performance Index', format: '0,0' },
        RecordCount: { friendlyName: 'Responses', format: '0' },
      },
    },
    count: 12,
  };
}

/**
 * Get mock data for NpsGaugeChart visual
 */
export async function getMockNpsGaugeChartData(): Promise<CrNpsGaugeChartData> {
  return {
    visualType: 'CrNpsGaugeChart',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          Msr_Svy_Data_NPS_IndexAverage: {
            CurrentValue: 0.67,
            PreviousValue: 0.66,
            Delta: 0.01,
          },
          RecordCount: {
            CurrentValue: 471.0,
            PreviousValue: 390.0,
            Delta: 81.0,
          },
        },
      },
      dimensionMetadata: {},
      measureMetadata: {
        Msr_Svy_Data_NPS_IndexAverage: {
          friendlyName: 'NPS',
          format: '0.00nps',
        },
        RecordCount: {
          friendlyName: 'Responses',
          format: '0',
        },
      },
    },
    count: 0,
  };
}

/**
 * Get mock data for MetricMovementTable visual
 */
export async function getMockMetricMovementTableData(): Promise<CrMetricMovementTableData> {
  return {
    visualType: 'CrMetricMovementTable',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          RecordCount: { CurrentValue: 485 },
          Msr_NPS_Score: { CurrentValue: 0.75, PreviousValue: 0.71, Delta: 0.04 },
        },
        rowDimension: {
          key: 'State',
          values: [
            {
              value: 'NSW',
              totalMeasures: {
                RecordCount: { CurrentValue: 120 },
                Msr_NPS_Score: { CurrentValue: 0.75, PreviousValue: 0.7, Delta: 0.05 },
              },
            },
            {
              value: 'ACT',
              totalMeasures: {
                RecordCount: { CurrentValue: 85 },
                Msr_NPS_Score: { CurrentValue: 0.68, PreviousValue: 0.72, Delta: -0.04 },
              },
            },
            {
              value: 'VIC',
              totalMeasures: {
                RecordCount: { CurrentValue: 95 },
                Msr_NPS_Score: { CurrentValue: 0.82, PreviousValue: 0.78, Delta: 0.04 },
              },
            },
            {
              value: 'QLD',
              totalMeasures: {
                RecordCount: { CurrentValue: 110 },
                Msr_NPS_Score: { CurrentValue: 0.71, PreviousValue: 0.69, Delta: 0.02 },
              },
            },
            {
              value: 'WA',
              totalMeasures: {
                RecordCount: { CurrentValue: 75 },
                Msr_NPS_Score: { CurrentValue: 0.79, PreviousValue: 0.76, Delta: 0.03 },
              },
            },
          ],
        },
      },
      dimensionMetadata: {
        State: { friendlyName: 'State' },
      },
      measureMetadata: {
        RecordCount: { friendlyName: 'Responses', format: '0' },
        Msr_NPS_Score: { friendlyName: 'NPS', format: '0nps' },
      },
    },
    count: 5,
  };
}

/**
 * Get mock data for NpsStackedBarWithLineChart visual
 */
export async function getMockNpsStackedBarWithLineChartData(): Promise<CrNpsStackedBarWithLineChartData> {
  return {
    visualType: 'CrNpsStackedBarWithLineChart',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 0.032520325203252036 },
          RecordCount: { CurrentValue: 471.0 },
          InternalRecordCount: { CurrentValue: 471.0 },
        },
        rowDimension: {
          key: 'Svy_Data_DateLastResponded_MonthOnAest',
          values: [
            {
              value: '2025-05',
              totalMeasures: {
                Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 0.13636363636363635 },
                RecordCount: { CurrentValue: 25.0 },
                InternalRecordCount: { CurrentValue: 25.0 },
              },
              columnDimension: {
                key: 'Svy_Data_NPS_Label',
                values: [
                  {
                    value: 'Detractor',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: -1.0 },
                      RecordCount: { CurrentValue: 6.0 },
                      InternalRecordCount: { CurrentValue: 6.0 },
                    },
                  },
                  {
                    value: 'Neutral',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 0.0 },
                      RecordCount: { CurrentValue: 7.0 },
                      InternalRecordCount: { CurrentValue: 7.0 },
                    },
                  },
                  {
                    value: 'Promoter',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 1.0 },
                      RecordCount: { CurrentValue: 9.0 },
                      InternalRecordCount: { CurrentValue: 9.0 },
                    },
                  },
                ],
              },
            },
            {
              value: '2025-06',
              totalMeasures: {
                Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 0.021739130434782608 },
                RecordCount: { CurrentValue: 120.0 },
                InternalRecordCount: { CurrentValue: 120.0 },
              },
              columnDimension: {
                key: 'Svy_Data_NPS_Label',
                values: [
                  {
                    value: 'Detractor',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: -1.0 },
                      RecordCount: { CurrentValue: 36.0 },
                      InternalRecordCount: { CurrentValue: 36.0 },
                    },
                  },
                  {
                    value: 'Neutral',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 0.0 },
                      RecordCount: { CurrentValue: 18.0 },
                      InternalRecordCount: { CurrentValue: 18.0 },
                    },
                  },
                  {
                    value: 'Promoter',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 1.0 },
                      RecordCount: { CurrentValue: 38.0 },
                      InternalRecordCount: { CurrentValue: 38.0 },
                    },
                  },
                ],
              },
            },
            {
              value: '2025-07',
              totalMeasures: {
                Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: -0.019417475728155338 },
                RecordCount: { CurrentValue: 128.0 },
                InternalRecordCount: { CurrentValue: 128.0 },
              },
              columnDimension: {
                key: 'Svy_Data_NPS_Label',
                values: [
                  {
                    value: 'Detractor',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: -1.0 },
                      RecordCount: { CurrentValue: 34.0 },
                      InternalRecordCount: { CurrentValue: 34.0 },
                    },
                  },
                  {
                    value: 'Neutral',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 0.0 },
                      RecordCount: { CurrentValue: 37.0 },
                      InternalRecordCount: { CurrentValue: 37.0 },
                    },
                  },
                  {
                    value: 'Promoter',
                    totalMeasures: {
                      Msr_Svy_Data_NPS_IndexAverage: { CurrentValue: 1.0 },
                      RecordCount: { CurrentValue: 32.0 },
                      InternalRecordCount: { CurrentValue: 32.0 },
                    },
                  },
                ],
              },
            },
          ],
        },
      },
      dimensionMetadata: {
        Svy_Data_DateLastResponded_MonthOnAest: { friendlyName: 'Response Date (Aest Month)' },
        Svy_Data_NPS_Label: { friendlyName: 'NPS Label' },
      },
      measureMetadata: {
        Msr_Svy_Data_NPS_IndexAverage: { friendlyName: 'NPS', format: '0.00nps' },
        RecordCount: { friendlyName: 'Responses', format: '0' },
        InternalRecordCount: { friendlyName: 'Internal Responses', format: '0' },
      },
    },
    count: 0,
  };
}

/**
 * Get mock data for SmoothedLineChart visual
 */
export async function getMockSmoothedLineChartData(): Promise<CrSmoothedLineChartData> {
  return {
    visualType: 'CrSmoothedLineChart',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          Msr_Sales: { CurrentValue: 2450 },
          Msr_Revenue: { CurrentValue: 18500 },
          Msr_Profit: { CurrentValue: 3200 },
          RecordCount: { CurrentValue: 120 },
        },
        columnDimension: {
          key: 'Month',
          values: [
            {
              value: 'Jan 2024',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 1200 },
                Msr_Revenue: { CurrentValue: 8500 },
                Msr_Profit: { CurrentValue: 1500 },
                RecordCount: { CurrentValue: 50 },
              },
            },
            {
              value: 'Feb 2024',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 1350 },
                Msr_Revenue: { CurrentValue: 9200 },
                Msr_Profit: { CurrentValue: 1650 },
                RecordCount: { CurrentValue: 55 },
              },
            },
            {
              value: 'Mar 2024',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 1100 },
                Msr_Revenue: { CurrentValue: 7800 },
                Msr_Profit: { CurrentValue: 1350 },
                RecordCount: { CurrentValue: 45 },
              },
            },
            {
              value: 'Apr 2024',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 1450 },
                Msr_Revenue: { CurrentValue: 10200 },
                Msr_Profit: { CurrentValue: 1800 },
                RecordCount: { CurrentValue: 60 },
              },
            },
            {
              value: 'May 2024',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 1600 },
                Msr_Revenue: { CurrentValue: 11500 },
                Msr_Profit: { CurrentValue: 1950 },
                RecordCount: { CurrentValue: 65 },
              },
            },
            {
              value: 'Jun 2024',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 1750 },
                Msr_Revenue: { CurrentValue: 12800 },
                Msr_Profit: { CurrentValue: 2100 },
                RecordCount: { CurrentValue: 70 },
              },
            },
          ],
        },
      },
      dimensionMetadata: {
        Month: { friendlyName: 'Month' },
      },
      measureMetadata: {
        Msr_Sales: { friendlyName: 'Sales Volume', format: '0,0' },
        Msr_Revenue: { friendlyName: 'Revenue', format: '$0,0' },
        Msr_Profit: { friendlyName: 'Profit', format: '$0,0' },
        RecordCount: { friendlyName: 'Transactions', format: '0,0' },
      },
    },
    count: 6,
  };
}

/**
 * Get mock data for CrDeltaBarChart visual
 */
export async function getMockDeltaBarChartData(): Promise<CrDeltaBarChartData> {
  return {
    visualType: 'CrDeltaBarChart',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          Msr_Sales: { CurrentValue: 2450, PreviousValue: 2200, Delta: 250 },
          Msr_Revenue: { CurrentValue: 18500, PreviousValue: 17200, Delta: 1300 },
          RecordCount: { CurrentValue: 120 },
        },
        rowDimension: {
          key: 'Region',
          values: [
            {
              value: 'North',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 650, PreviousValue: 600, Delta: 50 },
                Msr_Revenue: { CurrentValue: 4800, PreviousValue: 4500, Delta: 300 },
                RecordCount: { CurrentValue: 30 },
              },
            },
            {
              value: 'South',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 580, PreviousValue: 550, Delta: 30 },
                Msr_Revenue: { CurrentValue: 4200, PreviousValue: 4100, Delta: 100 },
                RecordCount: { CurrentValue: 28 },
              },
            },
            {
              value: 'East',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 720, PreviousValue: 680, Delta: 40 },
                Msr_Revenue: { CurrentValue: 5500, PreviousValue: 5200, Delta: 300 },
                RecordCount: { CurrentValue: 35 },
              },
            },
            {
              value: 'West',
              totalMeasures: {
                Msr_Sales: { CurrentValue: 500, PreviousValue: 370, Delta: 130 },
                Msr_Revenue: { CurrentValue: 4000, PreviousValue: 3400, Delta: 600 },
                RecordCount: { CurrentValue: 27 },
              },
            },
          ],
        },
      },
      dimensionMetadata: {
        Region: { friendlyName: 'Region' },
      },
      measureMetadata: {
        Msr_Sales: { friendlyName: 'Sales', format: '0,0' },
        Msr_Revenue: { friendlyName: 'Revenue', format: '$0,0' },
        RecordCount: { friendlyName: 'Transactions', format: '0,0' },
      },
      highlights: [
        {
          type: 'markPoint',
          semanticValue: 'custom',
          annotation: 'Strong sales performance',
          target: { measureKey: 'Msr_Sales' },
        },
        {
          type: 'markLine',
          semanticValue: 'custom',
          annotation: 'Revenue target threshold',
          target: { measureKey: 'Msr_Revenue' },
        },
      ],
    },
    count: 4,
  };
}

/**
 * Get mock data for CrMetricTileTable visual
 */
export async function getMockMetricTileTableData(): Promise<CrMetricTileTableData> {
  return {
    visualType: 'CrMetricTileTable',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          RecordCount: { CurrentValue: 485 },
          Msr_NPS_Score: { CurrentValue: 0.75, PreviousValue: 0.71, Delta: 0.04 },
        },
        rowDimension: {
          key: 'Quarter',
          values: [
            {
              value: 'Q1',
              totalMeasures: {
                RecordCount: { CurrentValue: 120 },
                Msr_NPS_Score: { CurrentValue: 0.75, PreviousValue: 0.7, Delta: 0.05 },
              },
            },
            {
              value: 'Q2',
              totalMeasures: {
                RecordCount: { CurrentValue: 85 },
                Msr_NPS_Score: { CurrentValue: 0.68, PreviousValue: 0.72, Delta: -0.04 },
              },
            },
            {
              value: 'Q3',
              totalMeasures: {
                RecordCount: { CurrentValue: 95 },
                Msr_NPS_Score: { CurrentValue: 0.82, PreviousValue: 0.78, Delta: 0.04 },
              },
            },
            {
              value: 'Q4',
              totalMeasures: {
                RecordCount: { CurrentValue: 110 },
                Msr_NPS_Score: { CurrentValue: 0.71, PreviousValue: 0.69, Delta: 0.02 },
              },
            },
          ],
        },
      },
      dimensionMetadata: {
        Quarter: { friendlyName: 'Quarter' },
      },
      measureMetadata: {
        RecordCount: { friendlyName: 'Responses', format: '0' },
        Msr_NPS_Score: { friendlyName: 'NPS', format: '0nps' },
      },
    },
    count: 5,
  };
}

/**
 * Get mock data for CrMetricDriverPriorityQuadrantChart visual
 */
export async function getMockCrMetricDriverPriorityQuadrantChartData(): Promise<CrMetricDriverPriorityQuadrantChartData> {
  return {
    visualType: 'CrMetricDriverPriorityQuadrantChart',
    data: {
      data: [
        {
          driverMeasureKey: 'Msr_Svy_Data_Availability_TopBoxAgreementIndexAverage',
          score: {
            current: 0.7482517482517482,
            previous: 0.720568783068783,
            delta: 0.027682965182965202,
          },
          importance: {
            current: 0.8583840177022791,
            previous: 0.8429832539335422,
            delta: 0.015400763768736891,
          },
          responseCount: 142,
          previousResponseCount: 2993,
          pValue: 0.0,
          isSignificant: true,
        },
        {
          driverMeasureKey: 'Msr_Svy_Data_Services_TopBoxAgreementIndexAverage',
          score: {
            current: 0.7412587412587412,
            previous: 0.7166005291005291,
            delta: 0.02465821215821218,
          },
          importance: {
            current: 0.8644485581984094,
            previous: 0.8362349943468855,
            delta: 0.02821356385152385,
          },
          responseCount: 142,
          previousResponseCount: 2993,
          pValue: 0.0,
          isSignificant: true,
        },
        {
          driverMeasureKey: 'Msr_Svy_Data_Information_TopBoxAgreementIndexAverage',
          score: {
            current: 0.7342657342657343,
            previous: 0.718584656084656,
            delta: 0.015681078181078223,
          },
          importance: {
            current: 0.8644485581984094,
            previous: 0.8472917656375297,
            delta: 0.017156792560879652,
          },
          responseCount: 142,
          previousResponseCount: 2993,
          pValue: 0.0,
          isSignificant: true,
        },
        {
          driverMeasureKey: 'Msr_Svy_Data_Price_TopBoxAgreementIndexAverage',
          score: {
            current: 0.7482517482517482,
            previous: 0.7192460317460317,
            delta: 0.029005716505716483,
          },
          importance: {
            current: 0.8525071701651712,
            previous: 0.8441775580585257,
            delta: 0.008329612106645512,
          },
          responseCount: 142,
          previousResponseCount: 2993,
          pValue: 0.0,
          isSignificant: true,
        },
        {
          driverMeasureKey: 'Msr_Svy_Data_Checkout_TopBoxAgreementIndexAverage',
          score: {
            current: 0.7412587412587412,
            previous: 0.720568783068783,
            delta: 0.020689958189958224,
          },
          importance: {
            current: 0.8463851013257955,
            previous: 0.8423861018710502,
            delta: 0.0039989994547452445,
          },
          responseCount: 142,
          previousResponseCount: 2993,
          pValue: 0.0,
          isSignificant: true,
        },
        {
          driverMeasureKey: 'Msr_Svy_Data_Delivery_TopBoxAgreementIndexAverage',
          score: {
            current: 0.7412587412587412,
            previous: 0.7199074074074074,
            delta: 0.02135133385133381,
          },
          importance: {
            current: 0.8644485581984094,
            previous: 0.8413483320835068,
            delta: 0.02310022611490259,
          },
          responseCount: 142,
          previousResponseCount: 2993,
          pValue: 0.0,
          isSignificant: true,
        },
      ],
      keyMetricMeasureKey: 'Msr_Svy_Data_NPS_IndexAverage',
      averageScore: 0.7424242424242425,
      averageImportance: 0.8584369939647457,
      dimensionMetadata: {
        Svy_Data_DateTimeLastResponded_AtAest: {
          friendlyName: 'Response Time (Aest)',
        },
      },
      measureMetadata: {
        Msr_Svy_Data_NPS_IndexAverage: {
          friendlyName: 'NPS',
          format: '0.00nps',
        },
        Msr_Svy_Data_Availability_TopBoxAgreementIndexAverage: {
          friendlyName: 'Availability',
          format: '0.00%',
        },
        Msr_Svy_Data_Services_TopBoxAgreementIndexAverage: {
          friendlyName: 'Services',
          format: '0.00%',
        },
        Msr_Svy_Data_Information_TopBoxAgreementIndexAverage: {
          friendlyName: 'Information',
          format: '0.00%',
        },
        Msr_Svy_Data_Price_TopBoxAgreementIndexAverage: {
          friendlyName: 'Price',
          format: '0.00%',
        },
        Msr_Svy_Data_Checkout_TopBoxAgreementIndexAverage: {
          friendlyName: 'Checkout',
          format: '0.00%',
        },
        Msr_Svy_Data_Delivery_TopBoxAgreementIndexAverage: {
          friendlyName: 'Delivery',
          format: '0.00%',
        },
      },
      filterValues: {
        App_BaseClaim: ['1'],
        Svy_Data_DateLastResponded_OnAest: ['2025-12-01T00:00:00'],
      },
      highlights: [],
      warnings: [],
    },
    count: 6,
  };
}

/**
 * Get mock data for CrMetricDeltaQuadrantChart visual
 */
export async function getMockMetricDeltaQuadrantChartData(): Promise<CrMetricDeltaQuadrantChartData> {
  return {
    visualType: 'CrMetricDeltaQuadrantChart',
    data: {
      rollupData: {
        value: '',
        totalMeasures: {
          Msr_NPS_Score: { CurrentValue: 0.72 },
          RecordCount: { CurrentValue: 485 },
        },
        columnDimension: {
          key: 'State',
          values: [
            {
              value: 'NSW',
              totalMeasures: {
                Msr_NPS_Score: { CurrentValue: 0.75, PreviousValue: 0.7, Delta: 0.05 },
                RecordCount: { CurrentValue: 120 },
              },
            },
            {
              value: 'VIC',
              totalMeasures: {
                Msr_NPS_Score: { CurrentValue: 0.82, PreviousValue: 0.78, Delta: 0.04 },
                RecordCount: { CurrentValue: 95 },
              },
            },
            {
              value: 'QLD',
              totalMeasures: {
                Msr_NPS_Score: { CurrentValue: 0.71, PreviousValue: 0.69, Delta: 0.02 },
                RecordCount: { CurrentValue: 110 },
              },
            },
            {
              value: 'WA',
              totalMeasures: {
                Msr_NPS_Score: { CurrentValue: 0.79, PreviousValue: 0.76, Delta: 0.03 },
                RecordCount: { CurrentValue: 75 },
              },
            },
            {
              value: 'SA',
              totalMeasures: {
                Msr_NPS_Score: { CurrentValue: 0.65, PreviousValue: 0.68, Delta: -0.03 },
                RecordCount: { CurrentValue: 45 },
              },
            },
            {
              value: 'TAS',
              totalMeasures: {
                Msr_NPS_Score: { CurrentValue: 0.68, PreviousValue: 0.72, Delta: -0.04 },
                RecordCount: { CurrentValue: 25 },
              },
            },
            {
              value: 'NT',
              totalMeasures: {
                Msr_NPS_Score: { CurrentValue: 0.62, PreviousValue: 0.65, Delta: -0.03 },
                RecordCount: { CurrentValue: 15 },
              },
            },
          ],
        },
      },
      dimensionMetadata: {
        State: { friendlyName: 'State' },
      },
      measureMetadata: {
        Msr_NPS_Score: { friendlyName: 'NPS Score', format: '0.00' },
        RecordCount: { friendlyName: 'Responses', format: '0' },
      },
    },
    count: 7,
  };
}

/**
 * Get mock text content based on visual key
 */
function getMockTextForVisualKey(visualKey?: string): string {
  if (!visualKey) {
    return 'Sample rendered text from server';
  }

  // Title texts
  if (visualKey.includes('title')) {
    if (visualKey === 'cover-title') {
      return 'Family Experience Program';
    } else if (visualKey === 'main-chart-title') {
      return 'NPS Overview';
    } else if (visualKey === 'two-content-title') {
      return 'Two Content Chart Demo';
    } else if (visualKey === 'two-content-chart2-title') {
      return 'Two Content Chart Demo';
    } else if (visualKey === 'clustered-column-title') {
      return 'Regional Sales Performance';
    } else if (visualKey === 'units-title') {
      return 'Store Performance Analysis';
    } else if (visualKey === 'vertical-layout-title') {
      return 'Vertical Charts Layout';
    } else if (visualKey === 'nps-gauge-title') {
      return 'NPS Score Summary';
    } else if (visualKey === 'metric-movement-title') {
      return 'NPS Movement by State';
    } else if (visualKey === 'delta-bar-chart-title') {
      return 'Delta Bar Chart - Current vs Delta Values';
    } else if (visualKey === 'smoothed-line-chart-title') {
      return 'Smoothed Line Chart - Trend Analysis';
    } else if (visualKey === 'metric-delta-quadrant-chart-title') {
      return 'Metric Delta Quadrant Chart - Performance vs Change Analysis';
    } else if (visualKey === 'horizontal-chart-text-title') {
      return 'Horizontal Chart & Text Layout';
    }
  }

  // Subtitle texts
  if (visualKey.includes('subtitle')) {
    return '2025 September Report';
  }

  // Text content
  if (visualKey.includes('text')) {
    if (visualKey === 'main-chart-text') {
      return 'This chart shows the Net Promoter Score trends over time.';
    } else if (visualKey === 'two-content-text' || visualKey === 'two-content-chart2-text') {
      return 'This demonstrates the new two-content layout with the same chart on both sides.';
    } else if (visualKey === 'clustered-column-text') {
      return 'This chart shows quarterly sales performance across different regions using a clustered column chart.';
    } else if (visualKey === 'units-text') {
      return 'Analysis of top and bottom performing stores based on sales metrics.';
    } else if (visualKey === 'vertical-layout-text') {
      return 'This slide demonstrates the new layout with two charts stacked vertically on the left and a text description on the right. This layout is useful for comparing two related metrics side-by-side with their explanation. This layout is useful for comparing two related metrics side-by-side with their explanation.';
    } else if (visualKey === 'nps-gauge-text') {
      return 'This gauge shows the current NPS score with comparison to the previous period.';
    } else if (visualKey === 'metric-movement-text') {
      return 'This table shows NPS movement across different states, sorted by delta from highest to lowest.';
    } else if (visualKey === 'delta-bar-chart-text') {
      return 'This chart displays multiple measures with their current values and deltas in clustered horizontal bars. Each measure shows two bars: current value (left) and delta (right).';
    } else if (visualKey === 'smoothed-line-chart-text') {
      return 'This smoothed line chart shows trends over time with multiple measures. The lines are smoothed for better visual clarity.';
    } else if (visualKey === 'metric-delta-quadrant-chart-text') {
      return 'This bubble chart plots categories by their current metric value (X-axis) and change versus previous period (Y-axis). Bubble size represents sample size. Quadrant shading helps identify performance patterns: top-right (high current + improving), bottom-right (high current + declining), bottom-left (low current + declining), top-left (low current + improving).';
    }
  }

  // Default fallback
  return 'Sample rendered text from server';
}

/**
 * Cleanup demo mocking - placeholder for future expansion
 */
export function cleanupDemoMocking() {
  // Demo mocking cleaned up
}
